import axios from 'axios';
import { message } from 'antd';

// 创建axios实例
const api = axios.create({
  baseURL: '',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 可以在这里添加token等认证信息
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    // 处理文件下载类型的响应（blob）
    if (response.config.responseType === 'blob') {
      return response.data;
    }
    
    const { data } = response;
    
    // 根据后端统一响应格式处理
    if (data.success) {
      return data.content;
    } else {
      // 静默模式下不弹出错误提示，由调用方自行处理
      if (!response.config._silent) {
        message.error(data.errorMsg || '请求失败');
      }
      return Promise.reject(new Error(data.errorMsg || '请求失败'));
    }
  },
  (error) => {
    // 静默模式下不弹出错误提示，由调用方自行处理
    if (!error.config?._silent) {
      const errorMessage = error.response?.data?.errorMsg || error.message || '网络错误';
      message.error(errorMessage);
    }
    return Promise.reject(error);
  }
);

// 业务域管理API
export const businessDomainApi = {
  // 获取业务域列表
  getList: (params) => api.get('/api/business-domain/list', { params }),
  
  // 获取业务域详情
  getDetail: (id) => api.get(`/api/business-domain/${id}`),
  
  // 新增业务域
  create: (data) => api.post('/api/business-domain', data),
  
  // 修改业务域
  update: (id, data) => api.post(`/api/business-domain/update/${id}`, data),
  
  // 删除业务域
  delete: (id) => api.post(`/api/business-domain/delete/${id}`),

  // 手动修改测试空间标记
  toggleTestMark: (id, businessType) => api.post(`/api/business-domain/toggle-test-mark/${id}`, { businessType }),
};

// 业务场景管理API
export const businessScenarioApi = {
  // 获取业务场景列表
  getList: (params) => api.get('/api/business-scenario/list', { params }),
  
  // 获取业务场景详情
  getDetail: (id) => api.get(`/api/business-scenario/${id}`),
  
  // 新增业务场景
  create: (data) => api.post('/api/business-scenario', data),
  
  // 修改业务场景
  update: (id, data) => api.post(`/api/business-scenario/update/${id}`, data),
  
  // 删除业务场景
  delete: (id) => api.post(`/api/business-scenario/delete/${id}`),

  // 根据项目空间ID获取业务场景列表（返回text/value格式）
  getListByDomain: (domainId) => api.get('/api/business-scenario/list-by-domain', { params: { domainId } }),
};

// 评测集管理API
export const judgeSetApi = {
  // 获取评测集列表
  getList: (params) => api.get('/api/judge-set/list', { params }),
  
  // 获取评测集详情
  getDetail: (id) => api.get(`/api/judge-set/${id}`),
  
  // 新增评测集
  create: (data) => api.post('/api/judge-set', data),
  
  // 修改评测集
  update: (id, data) => api.post(`/api/judge-set/update/${id}`, data),
  
  // 删除评测集
  delete: (id) => api.post(`/api/judge-set/delete/${id}`),
  
  // 获取评测集关联的用例列表
  getCases: (judgeSetId, params) => api.get(`/api/judge-set/${judgeSetId}/cases`, { params }),
  
  // 保存评测集与用例的关联关系
  saveCases: (judgeSetId, data) => api.post(`/api/judge-set/${judgeSetId}/cases`, data),
  
  // 移除评测集与用例的关联
  removeCases: (judgeSetId, data) => api.delete(`/api/judge-set/${judgeSetId}/cases`, { data }),
  
  // 获取评测集已关联的用例ID列表
  getAssociatedCaseIds: (judgeSetId) => api.get(`/api/judge-set/${judgeSetId}/case-ids`),
};

// 用例管理API
export const useCaseApi = {
  // 获取用例列表
  getList: (params) => api.get('/api/use-case/list', { params }),
  
  // 获取用例详情
  getDetail: (id) => api.get(`/api/use-case/${id}`),
  
  // 新增用例
  create: (data) => api.post('/api/use-case', data),
  
  // 修改用例
  update: (id, data) => api.post(`/api/use-case/update/${id}`, data),
  
  // 更新用例期望答案（仅更新expected_answer字段）
  updateExpectedAnswer: (id, data) => api.post(`/api/use-case/update-expected-answer/${id}`, typeof data === 'string' ? { expectedAnswer: data } : data),
  
  // 删除用例
  delete: (id) => api.post(`/api/use-case/delete/${id}`),
  
  // 单文件上传
  uploadFile: (formData, scenarioId) => api.post('/api/use-case/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    params: { scenarioId },
  }),
  
  
  // 批量导入用例
  batchImport: (data) => api.post('/api/use-case/batch-import', data),
  
  // 获取附件信息
  getAttachment: (useCaseId) => api.get(`/api/use-case/${useCaseId}/attachment`),
  
  // 导出用例
  exportCases: (params) => api.get('/api/use-case/export', { 
    params,
    responseType: 'blob'
  }),

  // 配置可见业务场景
  configVisibleScenarios: (useCaseId, data) => api.post(`/api/use-case/${useCaseId}/visible-scenarios`, data),

  // 配置可修改业务场景
  configEditableScenarios: (useCaseId, data) => api.post(`/api/use-case/${useCaseId}/editable-scenarios`, data),

  // 批量配置可见业务场景
  batchConfigVisibleScenarios: (data) => api.post('/api/use-case/batch-visible-scenarios', data),

  // 批量配置可修改业务场景
  batchConfigEditableScenarios: (data) => api.post('/api/use-case/batch-editable-scenarios', data),

  // 获取用例的可见业务场景
  getVisibleScenarios: (useCaseId) => api.get(`/api/use-case/${useCaseId}/visible-scenarios`),

  // 获取用例的可修改业务场景
  getEditableScenarios: (useCaseId) => api.get(`/api/use-case/${useCaseId}/editable-scenarios`),

  // Excel导入用例（要素抽取场景专用）- 异步导入，返回任务ID
  importExcel: (formData) => api.post('/api/use-case/batch-import-with-attachments', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),

  // 查询用例导入任务状态
  // 返回：status(PENDING/RUNNING/SUCCESS/FAILED), progress(0-100), totalCount, processedCount, successCount, failedCount, message, result
  getImportTaskStatus: (taskId) => api.get(`/api/use-case/import-task/${taskId}`),

  // 批量更新用例的可见业务场景和可修改业务场景
  batchUpdateBusinessScenarios: (data) => api.post('/api/use-case/batch-update-business-scenarios', data),

  // 批量添加评测集
  batchBindJudgeSets: (data) => api.post('/api/use-case/batch-bind-judge-sets', data),

  // 按业务场景批量绑定用例到评测集（一键全选）
  batchBindJudgeSetsByScenario: (data) => api.post('/api/use-case/batch-bind-judge-sets-by-scenario', data),

  // 通过评测集ID分页查询用例信息
  getByJudgeSet: (judgeSetId, params) => api.get(`/api/use-case/by-judge-set/${judgeSetId}`, { params }),

  // 删除用例和评测集的关联关系
  unbindJudgeSet: (data) => api.post('/api/use-case/unbind-judge-set', data),

  /**
   * 通过用例ID查询用例信息和对应的附件信息
   * @param id 用例ID
   * @return 用例信息和附件信息
   */
  getWithAttachment: (id) => api.get(`/api/use-case/${id}/with-attachment`),

  /**
   * 下载用例导入模板（Excel格式）
   * @return Excel模板文件
   */
  downloadTemplate: () => api.get('/api/use-case/download-template', {
    responseType: 'blob'
  }),

  /**
   * 机审场景批量导入用例预览
   * @param formData 包含Excel文件的FormData
   * @return 预览结果
   */
  autoReviewImportPreview: (formData) => api.post('/api/use-case/auto-review-import-preview', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),

  /**
   * 机审场景批量导入用例确认
   * @param previewDTO 预览结果
   * @return 导入结果
   */
  autoReviewImportConfirm: (previewDTO) => api.post('/api/use-case/auto-review-import-confirm', previewDTO),

  /**
   * 下载机审场景用例导入模板
   * @return Excel模板文件
   */
  downloadAutoReviewImportTemplate: () => api.get('/api/use-case/auto-review-import-template', {
    responseType: 'blob'
  }),

  /**
   * 批量删除用例
   * @param data { caseIds: number[] } 用例ID列表
   * @return 删除结果
   */
  batchDelete: (data) => api.post('/api/use-case/batch-delete', data),

  /**
   * 获取用例文件类型列表
   * @param caseIds 用例ID列表
   * @return 文件类型列表
   */
  getFileTypes: (caseIds) => api.post('/api/use-case/file-type', { caseIds }),

  /**
   * 根据业务场景ID查询所有用例的文件类型（包括共享业务场景中的用例）
   * @param scenarioId 业务场景ID
   * @return 附件类型列表(text,value格式的Map)，去重后的结果
   */
  getFileTypesByScenario: (scenarioId) => api.get(`/api/use-case/file-types-by-scenario/${scenarioId}`),

  /**
   * 调用AI模型进行提示词调试
   * @param caseId 用例ID
   * @param promptWord 提示词（支持占位符：${actualData}, ${expectedData}, ${expectedFileType}）
   * @return 模型调试结果
   */
  invokeAiModel: (data) => api.post('/api/use-case/invoke-ai-model', data),
};

// 评测场景管理API
export const judgeScenarioApi = {
  // 获取评测场景列表
  getList: (params) => api.get('/api/judge-scenario/list', { params }),
  
  // 获取评测场景详情
  getDetail: (id) => api.get(`/api/judge-scenario/${id}`),
  
  // 新增评测场景
  create: (data) => api.post('/api/judge-scenario', data),
  
  // 修改评测场景
  update: (id, data, testOrderNo) => api.post(`/api/judge-scenario/update/${id}`, data, { params: { testOrderNo } }),
  
  // 删除评测场景
  delete: (id) => api.post(`/api/judge-scenario/delete/${id}`),

  // 获取文档Schema（同步ALE指标）
  getDocumentSchema: (data) => api.post('/api/judge-scenario/get-document-schema', data),
  
  // 获取模型类型列表（用于断言脚本配置）
  getModelTypes: () => api.get('/api/judge-scenario/model-types'),

  // AI异步重新生成脚本
  aiRegenerateScriptAsync: (data) => api.post('/api/judge-scenario/ai-regenerate-script-async', data),

  // AI调试脚本
  aiDebugScript: (data) => api.post('/api/judge-scenario/ai-debug-script', data),


  // 查询异步任务状态
  getAsyncTaskStatus: (taskId) => api.get(`/api/judge-scenario/async-task-status/${taskId}`),

  // AI确认并应用脚本
  aiConfirmScript: (data) => api.post('/api/judge-scenario/ai-confirm-script', data),

  // AI一键生成所有脚本（异步）
  aiGenerateAllScriptsAsync: (data) => api.post('/api/judge-scenario/ai-generate-all-scripts-async', data),
  
  // 刷新机审测试数据
  refreshTestResult: (scenarioId, testOrderNo) => 
    api.post(`/api/judge-scenario/refresh-test-result/${scenarioId}`, null, { 
      params: { testOrderNo } 
    }),

  // 获取文档Schema（带alias）- 用于标注页字段编辑器初始化（静默模式，失败不报错）
  getDocumentSchemaWithAlias: (caseId, taskId) =>
    api.get('/api/judge-scenario/document-schema-with-alias', { params: { caseId, taskId }, _silent: true }),
};

// 评测任务管理API
export const judgeTaskApi = {
  // 获取评测任务列表
  getList: (params) => api.get('/api/judge-task/list', { params }),
  
  // 获取评测任务详情
  getDetail: (id) => api.get(`/api/judge-task/${id}`),
  
  // 新增评测任务
  create: (data) => api.post('/api/judge-task', data),
  
  // 修改评测任务
  update: (id, data) => api.post(`/api/judge-task/update/${id}`, data),
  
  // 删除评测任务
  delete: (id) => api.post(`/api/judge-task/delete/${id}`),
  
  // 执行评测任务
  execute: (id) => api.post(`/api/judge-task/${id}/execute`),
  
  // 导入执行结果
  importResult: (id, formData) => api.post(`/api/judge-task/${id}/import-result`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
};

// 评测任务执行历史API
export const judgeTaskExecutionApi = {
  // 获取执行历史列表
  getList: (params) => api.get('/api/task-execution/list', { params }),
  
  // 获取执行历史详情
  getDetail: (id) => api.get(`/api/task-execution/${id}`),

  // 重新计算执行结果
  recalculate: (id) => api.post(`/api/task-execution/${id}/recalculate`),
};

// 用例执行历史API
export const useCaseExecutionApi = {
  // 获取用例执行历史列表
  getList: (params) => api.get('/api/use-case-execution/list', { params }),

  /**
   * 解析ALE返回的数据为简化格式
   * @param aleResultJson ALE接口返回的JSON字符串
   * @return 解析后的简化格式数据
   */
  parseAleResult: (aleResultJson) => api.post('/api/use-case-execution/parse-ale-result', { aleResultJson }),

  /**
   * 更新用例执行历史的标注结果
   * 接口路径: POST /api/use-case-execution/update-mark-result
   * @param id 用例执行历史ID
   * @param markResult 标注结果（JSON格式）
   * @return 更新结果
   */
  updateMarkResult: (data) => api.post('/api/use-case-execution/update-mark-result', data),

  /**
   * 根据任务执行历史ID统计用例断言通过率
   * 接口路径: GET /api/use-case-execution/pass-rate/{taskExecutionHistoryId}
   * @param taskExecutionHistoryId 任务执行历史ID
   * @param totalCount 用例总数（可选，不传则自动查询）
   * @return 通过率统计结果，包含 totalCount, passCount, failCount, passRate
   */
  getPassRate: (taskExecutionHistoryId, totalCount) => {
    const params = totalCount ? { totalCount } : {};
    return api.get(`/api/use-case-execution/pass-rate/${taskExecutionHistoryId}`, { params });
  },

  /**
   * 下载用例执行报告（机审场景）
   * 接口路径: GET /api/use-case-execution/download-report/{taskExecutionHistoryId}
   * @param taskExecutionHistoryId 任务执行历史ID
   * @return Excel文件流
   */
  downloadReport: (taskExecutionHistoryId) => {
    return api.get(`/api/use-case-execution/download-report/${taskExecutionHistoryId}`, {
      responseType: 'blob'
    });
  },

  /**
   * 回传用例执行报告（机审场景）
   * 接口路径: POST /api/use-case-execution/upload-report/{taskExecutionHistoryId}
   * @param taskExecutionHistoryId 任务执行历史ID
   * @param file Excel文件
   * @return 回传结果
   */
  uploadReport: (taskExecutionHistoryId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/use-case-execution/upload-report/${taskExecutionHistoryId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * 解析标注跳转URL（机审场景）
   * 接口路径: GET /api/use-case-execution/parse-marking-url
   * @param scenarioId 评测场景ID
   * @param caseId 用例ID
   * @param executionHistoryId 用例执行历史ID（可选）
   * @return 解析后的完整URL
   */
  parseMarkingUrl: (scenarioId, caseId, executionHistoryId) => {
    const params = { scenarioId, caseId };
    if (executionHistoryId) {
      params.executionHistoryId = executionHistoryId;
    }
    return api.get('/api/use-case-execution/parse-marking-url', { params });
  },

  /**
   * 批量重跑用例（仅重跑断言结果，不执行指标计算脚本）
   * 接口路径: POST /api/use-case-execution/rerun
   * @param ids 用例执行历史ID列表
   * @return 重跑结果
   */
  rerun: (ids) => api.post('/api/use-case-execution/rerun', { ids }),
};

// Benchmark管理API
export const benchmarkApi = {
  // 获取Benchmark列表
  getList: (params) => api.get('/api/benchmark/list', { params }),
  
  // 获取Benchmark详情
  getDetail: (id) => api.get(`/api/benchmark/${id}`),
  
  // 新建Benchmark
  create: (data) => api.post('/api/benchmark', data),
  
  // 修改Benchmark
  update: (id, data) => api.post(`/api/benchmark/update/${id}`, data),
  
  // 删除Benchmark
  delete: (id) => api.post(`/api/benchmark/delete/${id}`),
  
  // 用例添加到评测集
  addCases: (id, data) => api.post(`/api/benchmark/${id}/add-cases`, data),
  
  // 单文件上传 - 与用例上传保持一致的配置
  uploadFile: (formData) => api.post('/api/attachment/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // 根据任务执行历史ID获取指标配置
  getMetricsByExecutionHistory: (historyId) => 
    api.get(`/api/task-execution/${historyId}/metrics`),
};

// 标注结果管理API
export const annotationApi = {
  // 获取标注结果列表
  getList: (params) => api.get('/api/annotation/list', { params }),
  
  // 获取标注结果详情
  getDetail: (id) => api.get(`/api/annotation/${id}`),
  
  // 导入标注结果
  import: (formData) => api.post('/api/annotation/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // 批量更新用例标注结果
  batchUpdate: (formData) => api.post('/api/use-case/batch-update', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
};

// Benchmark指标管理API
export const benchmarkMetricApi = {
  // 获取指标列表
  getList: (params) => api.get('/api/benchmark-metric/list', { params }),
  
  // 获取指标详情
  getDetail: (id) => api.get(`/api/benchmark-metric/${id}`),
  
  // 新建指标
  create: (data) => api.post('/api/benchmark-metric', data),
  
  // 修改指标
  update: (id, data) => api.post(`/api/benchmark-metric/update/${id}`, data),
  
  // 删除指标
  delete: (id) => api.post(`/api/benchmark-metric/delete/${id}`),
};

// 评测场景指标管理API
export const scenarioMetricApi = {
  // 根据场景ID获取指标列表（不分页）
  getListByScenarioId: (scenarioId) => api.get(`/api/judge-scenario-metric/scenario/${scenarioId}`),
  
  // 获取指标配置（返回分类好的 presetMetrics 和 customMetrics）
  getConfig: (scenarioId) => api.get(`/api/judge-scenario-metric/config/${scenarioId}`),
  
  // 保存指标配置
  saveConfig: (scenarioId, data) => api.post(`/api/judge-scenario-metric/save-config/${scenarioId}`, data),
  
  // 新建指标
  create: (data) => api.post('/api/judge-scenario-metric', data),
  
  // 修改指标
  update: (id, data) => api.post(`/api/judge-scenario-metric/update/${id}`, data),
  
  // 删除指标
  delete: (id) => api.post(`/api/judge-scenario-metric/delete/${id}`),
};

// ============================================
// Dashboard 工作台首页统计接口
// ============================================
export const dashboardApi = {
  /**
   * 接口1: 获取核心概览统计
   * 请求路径: GET /api/dashboard/overview-stats
   * 描述: 获取项目空间、业务场景、用例总数（顶部三列彩色卡片）
   * 
   * 响应数据格式:
   * {
   *   totalDomains: number,      // 项目空间总数
   *   totalScenarios: number,    // 业务场景总数
   *   totalUseCases: number      // 用例总数
   * }
   */
  getOverviewStats: () => api.get('/api/dashboard/overview-stats'),

  /**
   * 接口2: 获取任务执行统计
   * 请求路径: GET /api/dashboard/task-stats
   * 描述: 获取评测任务、Benchmark相关统计数据（顶部三列白色卡片 + 评测任务执行统计区块）
   * 
   * 前端展示字段:
   *   - 顶部卡片: totalTasks(评测任务总数)、totalBenchmarks(Benchmark总数)、passRate(用例通过率，由接口5计算)
   *   - 评测任务执行统计: totalTasks(任务总数)、runningTasks(执行中任务)、avgExecutionTime(平均执行时长)
   * 
   * 响应数据格式:
   * {
   *   totalTasks: number,         // 评测任务总数
   *   totalBenchmarks: number,    // Benchmark总数
   *   runningTasks: number,       // 执行中任务数
   *   completedTasks: number,     // 成功任务数（暂未展示）
   *   failedTasks: number,        // 失败任务数（暂未展示）
   *   pendingTasks: number,       // 待执行任务数（暂未展示）
   *   successRate: number,        // 整体成功率（暂未展示）
   *   avgExecutionTime: number    // 平均执行时长(秒)
   * }
   */
  getTaskStats: () => api.get('/api/dashboard/task-stats'),

  /**
   * 接口3: 获取任务状态分布
   * 请求路径: GET /api/dashboard/task-status-distribution
   * 描述: 获取任务状态分布数据（暂未展示，饼图已移除）
   * 
   * 响应数据格式:
   * {
   *   distribution: [
   *     { status: '执行成功', count: number, color: '#22c55e' },
   *     { status: '执行中', count: number, color: '#f59e0b' },
   *     { status: '执行失败', count: number, color: '#ef4444' },
   *     { status: '待执行', count: number, color: '#94a3b8' }
   *   ]
   * }
   */
  getTaskStatusDistribution: () => api.get('/api/dashboard/task-status-distribution'),

  /**
   * 接口4: 获取项目空间统计分析
   * 请求路径: GET /api/dashboard/domain-radar
   * 描述: 获取各项目空间的能力雷达图数据
   * 
   * 响应数据格式:
   * {
   *   radarData: [
   *     {
   *       domain: string,        // 项目空间名称
   *       scenarios: number,     // 场景数(0-100归一化)
   *       tasks: number,         // 任务数(0-100归一化)
   *       success: number,       // 成功率(0-100)
   *       benchmarks: number     // Benchmark数(0-100归一化)
   *     }
   *   ]
   * }
   */
  getDomainRadar: () => api.get('/api/dashboard/domain-radar'),

  /**
   * 接口5: 获取用例执行详情统计
   * 请求路径: GET /api/dashboard/case-execution-stats
   * 描述: 获取用例执行详情统计数据
   * 
   * 前端展示文案:
   *   - 断言通过 -> 断言通过用例总数
   *   - 断言不通过 -> 断言不通过用例总数
   *   - 执行异常 -> 执行异常用例总数
   * 
   * 响应数据格式:
   * {
   *   executionStats: [
   *     { 
   *       status: '断言通过',       // 前端显示为: 断言通过用例总数
   *       count: number, 
   *       color: '#22c55e', 
   *       percentage: number,
   *       relatedScenarios: string[]  // 关联场景列表（暂未展示）
   *     },
   *     { status: '断言不通过', count: number, color: '#ef4444', percentage: number, relatedScenarios: string[] },
   *     { status: '执行异常', count: number, color: '#f59e0b', percentage: number, relatedScenarios: string[] }
   *   ],
   *   passRate: number           // 用例通过率(百分比)
   * }
   */
  getCaseExecutionStats: () => api.get('/api/dashboard/case-execution-stats'),

  /**
   * 接口6: 获取Benchmark版本演进列表（支持分页）
   * 请求路径: GET /api/dashboard/benchmark-evolution
   * 描述: 获取最新的Benchmark版本列表
   * 请求参数:
   *   - pageNo?: number (可选，页码，默认1)
   *   - pageSize?: number (可选，每页条数，默认5)
   * 
   * 响应数据格式:
   * {
   *   total: number,                  // 总条数
   *   benchmarks: [
   *     {
   *       id: number,
   *       benchmarkVersion: string,     // 版本号
   *       benchmarkType: string,        // 类型: 'BASELINE'|'ACCEPTANCE'|'PERIODIC'（展示时转换为中文label）
   *       businessScenarioName: string, // 业务场景名称
   *       useCaseCount: number,         // 用例数量
   *       gmtCreate: string             // 创建时间 YYYY-MM-DD HH:mm:ss
   *     }
   *   ]
   * }
   */
  getBenchmarkEvolution: (params) => api.get('/api/dashboard/benchmark-evolution', { params }),

  /**
   * 接口7: 获取业务场景分类统计
   * 请求路径: GET /api/dashboard/scenario-category-stats
   * 描述: 获取业务场景分类统计数据（要素抽取、机审、机器人问答）
   * 
   * 响应数据格式:
   * {
   *   totalScenarios: number,        // 业务场景总数
   *   categoryStats: [
   *     {
   *       category: string,          // 分类名称: '要素抽取'|'机审'|'机器人问答'
   *       categoryKey: string,       // 分类标识: 'FEATURE_EXTRACTION'|'AUTO_REVIEW'|'ROBOT_QA'
   *       scenarioCount: number,     // 该分类下场景数量
   *       judgeSetCount: number,     // 评测集数量
   *       useCaseCount: number,      // 用例数量
   *       taskCount: number,         // 评测任务数量
   *       passRate: number           // 用例通过率(百分比)
   *     }
   *   ]
   * }
   */
  getScenarioCategoryStats: () => api.get('/api/dashboard/scenario-category-stats'),
};

// 用户信息查询
export const userApi = {
  // 获取管理员用户信息
  getUserInfo: () => api.get('/api/user/info'),

  // 获取管理员用户信息
  logoutAdminUser: () => api.get('/api/user/logout'),

  /**
   * 获取当前用户在指定业务域和业务场景中的最高角色
   * 角色权限级别：项目空间管理员 > 业务场景管理员 > 项目成员 > 无
   * @param domainId 业务域ID（必填）
   * @param scenarioId 业务场景ID（可选）
   * @return 用户的最高角色
   * 返回值: NONE(0)-无权限, DOMAIN_MEMBER(1)-项目成员, SCENARIO_ADMIN(2)-业务场景管理员, DOMAIN_ADMIN(3)-项目空间管理员
   */
  getUserRole: (domainId, scenarioId) => {
    const params = { domainId };
    if (scenarioId) {
      params.scenarioId = scenarioId;
    }
    return api.get('/api/user/role', { params });
  },

  /**
   * 校验当前用户是否为指定业务场景的管理员
   * @param scenarioId 业务场景ID 必传参数
   * @return 校验结果，true表示是管理员，false表示不是管理员
   */
  checkScenarioAdmin: (scenarioId) => api.get('/api/user/check-scenario-admin', { params: { scenarioId } })
}

// 影子链路API
export const shadowLinkApi = {
  // 获取影子上报列表
  getReportList: (params) => api.get('/api/shadow-link/report/list', { params }),

  // 获取影子上报统计数据
  getReportStats: (params) => api.get('/api/shadow-link/report/stats', { params }),

  // 获取影子上报详情
  getReportDetail: (id) => api.get(`/api/shadow-link/report/${id}`),

  // 导出影子上报数据
  exportReport: (params) => api.get('/api/shadow-link/report/export', { params, responseType: 'blob' }),

  // 获取评测报告列表
  getEvaluationList: (params) => api.get('/api/shadow-link/evaluation/list', { params }),

  // 获取评测报告统计数据
  getEvaluationStats: (params) => api.get('/api/shadow-link/evaluation/stats', { params }),

  // 获取评测报告趋势数据
  getEvaluationTrend: (params) => api.get('/api/shadow-link/evaluation/trend', { params }),

  // 获取评测报告详情
  getEvaluationDetail: (id) => api.get(`/api/shadow-link/evaluation/${id}`),

  // 导出评测报告
  exportEvaluation: (id) => api.get(`/api/shadow-link/evaluation/${id}/export`, { responseType: 'blob' }),

  /**
   * 获取影子链路业务域列表（用于筛选）
   * 请求路径: GET /api/shadow-link/business-domains
   * 响应数据格式:
   * {
   *   list: [
   *     { value: '采购', label: '采购' },
   *     { value: '合同', label: '合同' },
   *     { value: '财务', label: '财务' }
   *   ]
   * }
   */
  getBusinessDomains: () => api.get('/api/shadow-link/business-domains'),

  /**
   * 根据业务域获取业务场景列表（用于筛选联动）
   * 请求路径: GET /api/shadow-link/business-scenarios
   * 请求参数: domain (可选，业务域值)
   * 响应数据格式:
   * {
   *   list: [
   *     { value: '采购RT审核-shadow', label: '采购RT审核-shadow' },
   *     { value: '合同机审-shadow', label: '合同机审-shadow' }
   *   ]
   * }
   */
  getBusinessScenarios: (domain) => api.get('/api/shadow-link/business-scenarios', { params: domain ? { domain } : {} }),

  // ==================== 断言配置 API ====================
  
  /**
   * 获取断言配置列表
   * GET /api/shadow-link/assertion-config/list
   * @param params {Object} - 查询参数
   *   - ruleName: 规则名称关键词（模糊搜索）
   *   - domainId: 业务域ID筛选
   *   - scenarioId: 业务场景ID筛选
   *   - assertionType: 断言类型 ENGINEERING / LLM
   *   - onlyMine: 是否只查我的规则
   *   - pageNum: 页码，默认1
   *   - pageSize: 每页条数，默认10
   */
  getAssertionConfigList: (params) => api.get('/api/shadow-link/assertion-config/list', { params }),

  /**
   * 创建断言规则
   * POST /api/shadow-link/assertion-config/create
   * @param data {Object} - 规则数据
   *   - ruleName: 规则名称
   *   - domainId: 业务域ID
   *   - scenarioId: 业务场景ID
   *   - assertionType: ENGINEERING 或 LLM
   *   - assertionCode: 工程断言代码（ENGINEERING时必填）
   *   - aiOutputPath: AI输出字段路径（LLM时必填）
   *   - humanOutputPath: 人工决策字段路径（LLM时必填）
   *   - semanticDescription: 语义对比描述（LLM时选填）
   *   - judgeModel: 判断模型（LLM时必填）
   *   - description: 规则备注
   */
  createAssertionConfig: (data) => api.post('/api/shadow-link/assertion-config/create', data),

  /**
   * 更新断言规则
   * POST /api/shadow-link/assertion-config/update
   * @param data {Object} - 规则数据（需包含id字段）
   */
  updateAssertionConfig: (data) => api.post('/api/shadow-link/assertion-config/update', data),

  /**
   * 删除断言规则（逻辑删除）
   * POST /api/shadow-link/assertion-config/delete
   * @param id {number} - 规则ID
   */
  deleteAssertionConfig: (id) => api.post('/api/shadow-link/assertion-config/delete', null, { params: { id } }),

  /**
   * 启用/禁用断言规则
   * POST /api/shadow-link/assertion-config/toggle-enabled
   * @param id {number} - 规则ID
   * @param enabled {string} - Y-启用，N-禁用
   */
  toggleAssertionConfig: (id, enabled) => api.post('/api/shadow-link/assertion-config/toggle-enabled', null, { params: { id, enabled } }),

  /**
   * 复制断言规则
   * POST /api/shadow-link/assertion-config/copy
   * @param id {number} - 被复制的规则ID
   * @returns {number} - 新复制出的规则ID
   */
  copyAssertionConfig: (id) => api.post('/api/shadow-link/assertion-config/copy', null, { params: { id } }),

  /**
   * 获取断言配置统计概览
   * GET /api/shadow-link/assertion-config/stats
   * @returns {Object} - 统计数据
   *   - engineeringCount: 工程断言规则总数
   *   - llmCount: LLM断言规则总数
   *   - enabledCount: 已启用规则数
   *   - myCount: 当前用户维护的规则数
   */
  getAssertionConfigStats: () => api.get('/api/shadow-link/assertion-config/stats'),
};

// =============================================
// 评测数据洞察 API
// =============================================
export const dataInsightApi = {
  /**
   * 获取当前用户有权限的项目空间列表(不分页)
   * GET /api/data-insight/authorized-domains
   * 返回格式: [{text: "项目空间名称", value: 项目空间ID}]
   */
  getAuthorizedDomains: () => api.get('/api/data-insight/authorized-domains'),

  /**
   * 根据项目空间ID列表获取机审业务场景列表
   * POST /api/data-insight/machine-audit-scenarios
   * 返回格式: [{text: "业务场景名称", value: 业务场景ID}]
   */
  getMachineAuditScenarios: (domainIds) => api.post('/api/data-insight/machine-audit-scenarios', domainIds),

  /**
   * 根据业务场景ID列表获取自定义指标列表(去重)
   * POST /api/data-insight/custom-metrics
   * 返回格式: [{text: "指标名称", value: "指标code"}]
   */
  getCustomMetrics: (scenarioIds) => api.post('/api/data-insight/custom-metrics', scenarioIds),

  /**
   * 数据过滤接口
   * POST /api/data-insight/filter
   * 入参:
   * - domainIds: 项目空间ID集合
   * - scenarioIds: 业务场景ID集合
   * - evalTypes: 评估类型集合(BASELINE/ACCEPTANCE/PERIODIC)
   * - watermarkDateRange: 水位时间范围 [startDate, endDate]
   * - sampleDateRange: 样本时间范围 [startDate, endDate]
   * - metricCodes: 指标选择集合
   * - customMetricCodes: 自定义指标集合
   */
  filterData: (params) => api.post('/api/data-insight/filter', params),
};

// Skill评测洞察 API
export const skillInsightApi = {
  // 获取领域列表
  getDomains: () => api.get('/api/skill-insight/domains'),
  // 获取Skill列表
  getSkills: () => api.get('/api/skill-insight/skills'),
  // 获取趋势数据
  getTrendData: (params) => api.post('/api/skill-insight/trend', params),
  // 获取评测记录列表
  getEvalRecords: (params) => api.post('/api/skill-insight/records', params),
  // 获取评测集详情
  getEvalSetDetail: (recordId) => api.get(`/api/skill-insight/eval-set/${recordId}`),
};

export default api;
